import React, { useEffect, useState } from 'react';
import { FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';

export const INVOICE_ADDRESS_FIELDS = [
  'invoice_name',
  'invoice_street',
  'invoice_street_number',
  'invoice_postal_code',
  'invoice_city',
  'invoice_country',
] as const;

export type InvoiceAddressField = (typeof INVOICE_ADDRESS_FIELDS)[number];

export function hasStoredInvoiceAddress(data: {
  invoice_name?: string | null;
  invoice_street?: string | null;
  invoice_street_number?: string | null;
  invoice_postal_code?: string | null;
  invoice_city?: string | null;
}): boolean {
  return Boolean(
    (data.invoice_name || '').trim() ||
      (data.invoice_street || '').trim() ||
      (data.invoice_street_number || '').trim() ||
      (data.invoice_postal_code || '').trim() ||
      (data.invoice_city || '').trim()
  );
}

export function clearInvoiceAddressFields(setValue: UseFormSetValue<any>): void {
  for (const field of INVOICE_ADDRESS_FIELDS) {
    setValue(field, '');
  }
}

/** Persist invoice address only when recipient/address fields are set; otherwise clear. */
export function invoiceAddressPayload(data: {
  invoice_name?: string;
  invoice_street?: string;
  invoice_street_number?: string;
  invoice_postal_code?: string;
  invoice_city?: string;
  invoice_country?: string;
}): Record<InvoiceAddressField, string> {
  if (!hasStoredInvoiceAddress(data)) {
    return {
      invoice_name: '',
      invoice_street: '',
      invoice_street_number: '',
      invoice_postal_code: '',
      invoice_city: '',
      invoice_country: '',
    };
  }
  return {
    invoice_name: data.invoice_name || '',
    invoice_street: data.invoice_street || '',
    invoice_street_number: data.invoice_street_number || '',
    invoice_postal_code: data.invoice_postal_code || '',
    invoice_city: data.invoice_city || '',
    invoice_country: data.invoice_country || '',
  };
}

interface InvoiceBillingSectionProps {
  register: UseFormRegister<any>;
  errors: FieldErrors;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  /** When editing, pass true if DB already has invoice address data */
  initialDifferentAddress?: boolean;
}

export const InvoiceBillingSection: React.FC<InvoiceBillingSectionProps> = ({
  register,
  errors,
  setValue,
  watch,
  initialDifferentAddress = false,
}) => {
  const [differentAddress, setDifferentAddress] = useState(initialDifferentAddress);

  useEffect(() => {
    setDifferentAddress(initialDifferentAddress);
  }, [initialDifferentAddress]);

  const handleToggle = (checked: boolean) => {
    setDifferentAddress(checked);
    if (!checked) {
      clearInvoiceAddressFields(setValue);
    } else if (!watch('invoice_country')) {
      setValue('invoice_country', 'Germany');
    }
  };

  return (
    <div className="border-t border-gray-200 pt-6 space-y-4">
      <h3 className="text-lg font-bold text-gray-900">Invoice / Billing</h3>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
          Owner (Inhaber)
        </label>
        <input
          type="text"
          {...register('owner_name')}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          placeholder="e.g. Max Mustermann"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
          Invoice Email
        </label>
        <input
          type="email"
          {...register('invoice_email')}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          placeholder="rechnung@beispiel.de"
        />
        {errors.invoice_email && (
          <p className="mt-1 text-sm text-red-600">
            {errors.invoice_email.message as string}
          </p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          Shown on delivery notes for manufacturer invoicing
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            Tax Number (Steuernummer)
          </label>
          <input
            type="text"
            {...register('tax_number')}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-base"
            placeholder="e.g. 12/345/67890"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            VAT ID (USt-IdNr)
          </label>
          <input
            type="text"
            {...register('vat_id')}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-base"
            placeholder="e.g. DE123456789"
          />
        </div>
      </div>

      <label className="flex items-start gap-3 cursor-pointer select-none rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
        <input
          type="checkbox"
          checked={differentAddress}
          onChange={(e) => handleToggle(e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <span>
          <span className="block text-sm font-semibold text-gray-900">
            Different billing address
          </span>
          <span className="block text-xs text-gray-500 mt-0.5">
            Enable if the invoice recipient or address differs from the store location
          </span>
        </span>
      </label>

      {differentAddress && (
        <div className="space-y-4 rounded-lg border border-blue-100 bg-blue-50/40 p-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Invoice recipient name
            </label>
            <input
              type="text"
              {...register('invoice_name')}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
              placeholder="Legal / billing name"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Street
              </label>
              <input
                type="text"
                {...register('invoice_street')}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
                placeholder="e.g. Musterstraße"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Number
              </label>
              <input
                type="text"
                {...register('invoice_street_number')}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
                placeholder="12"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Postal Code
              </label>
              <input
                type="text"
                {...register('invoice_postal_code')}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
                placeholder="10115"
                maxLength={10}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                City
              </label>
              <input
                type="text"
                {...register('invoice_city')}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
                placeholder="Berlin"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Country
            </label>
            <select
              {...register('invoice_country')}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
            >
              <option value="Germany">Germany</option>
              <option value="Austria">Austria</option>
              <option value="Switzerland">Switzerland</option>
              <option value="Netherlands">Netherlands</option>
              <option value="Poland">Poland</option>
              <option value="Czech Republic">Czech Republic</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};
